import React from 'react';
import Sparkline from './Sparkline';

// The child-simple observation card (Home "What's moving", 2026-10 restyle).
// Reads top to bottom like a sentence a kid could follow:
//   kind dot + when  →  which number  →  the big number with a coloured arrow
//   →  one plain sentence  →  the chart.
// The whole card is one tap target. Depth (every mover, your answer, Ask)
// lives behind the tap, never on the card.

const KIND_WORD = { today: 'Today', shift: 'Changed', tendency: 'Pattern', stack: 'Since you started' };

const dayWord = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === 2) return 'Two days ago';
  if (diff > 2 && diff < 7) return `${diff} days ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// The row above the metric: "Since you started magnesium" / "Yesterday" /
// "Changed · Jul 2" / "Pattern".
export const whenLabel = (item) => {
  if (item.kind === 'stack') return `${KIND_WORD.stack} ${item.item?.name || ''}`.trim();
  if (item.kind === 'today') return dayWord(item.when);
  if (item.kind === 'shift') return `${KIND_WORD.shift} · ${dayWord(item.when)}`;
  return KIND_WORD[item.kind] || item.kind_label;
};

const round1 = (v) => (Math.abs(v) >= 20 || Number.isInteger(v) ? Math.round(v) : Math.round(v * 10) / 10);

const UNIT_WORD = { bpm: 'beats', '/100': 'out of 100', '%': '%', h: 'hours', min: 'minutes', kg: 'kg', lb: 'lb', steps: 'steps' };

// Big number + small words, derived from the chart data (never from a model):
//  • a single highlighted day  →  that day's value, e.g. "42 out of 100"
//  • a highlighted window      →  how far the window sits from your usual,
//                                 e.g. "3 beats lower"
// Returns null when the chart can't support a number (groups, no points).
export const bigNumber = (item) => {
  const c = item.chart;
  if (!c || c.type !== 'series' || !c.points?.length) return null;
  const [hs, he] = c.highlight || [];
  const unit = c.unit || '';
  const word = UNIT_WORD[unit] ?? unit;
  if (hs && hs === he) {
    const p = c.points.find((x) => x.date === hs);
    if (!p || p.value == null) return null;
    const dir = c.baseline != null ? (p.value > c.baseline ? 'up' : p.value < c.baseline ? 'down' : null) : null;
    // A spread/variability metric means nothing as a raw number to most people,
    // so say how many times your usual swing it was ("8× more up and down").
    const half = c.band ? (c.band[1] - c.band[0]) / 2 : 0;
    if (/spread|range|variab/i.test(c.label || '') && half > 0 && c.baseline != null) {
      const times = Math.round(Math.abs(p.value - c.baseline) / half);
      if (times >= 2) return { text: `${times}×`, small: 'more up and down', dir };
    }
    const n = round1(p.value);
    const text = unit === '%' ? `${n}%` : `${n}`;
    return { text, small: unit === '%' ? '' : word, dir };
  }
  if (c.recent != null && c.baseline != null) {
    const delta = c.recent - c.baseline;
    if (delta === 0) return { text: 'Same', small: 'as before', dir: null };
    const dir = delta > 0 ? 'up' : 'down';
    const n = round1(Math.abs(delta));
    const text = unit === '%' ? `${n}%` : `${n}`;
    return { text, small: `${unit === '%' ? '' : word} ${dir === 'up' ? 'higher' : 'lower'}`.trim(), dir };
  }
  return null;
};

const Arrow = ({ dir, valence, size }) => {
  if (!dir) return null;
  const tone = valence === 'better' ? 'text-good-strong dark:text-good' : valence === 'worse' ? 'text-off-strong dark:text-off' : 'text-fg-3';
  return <span className={`${tone} font-normal ${size}`} aria-hidden="true">{dir === 'up' ? '↑' : '↓'}</span>;
};

const MovingCard = ({ item, featured = false, onOpen }) => {
  if (!item) return null;
  const dot = item.valence === 'better' ? 'bg-good-strong dark:bg-good' : item.valence === 'worse' ? 'bg-off-strong dark:bg-off' : 'bg-fg-3';
  const big = bigNumber(item);
  const sentence = (item.headline || '').replace(/\.$/, '');

  return (
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className={`group relative flex w-full min-w-0 flex-col text-left rounded-xl border border-line bg-raised transition-colors hover:border-fg-3 hover:bg-elev focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fg-3 ${featured ? 'p-5' : 'p-4 sm:p-[18px]'}`}
      aria-label={`${whenLabel(item)}: ${sentence}. See why.`}
    >
      <span className="absolute right-[18px] top-4 text-fg-3 group-hover:text-fg" aria-hidden="true">→</span>

      <span className="inline-flex items-center gap-2 text-sm font-medium text-fg-3">
        <i className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        {whenLabel(item)}
      </span>

      <span className={`font-medium text-fg-2 ${featured ? 'mt-1.5' : 'mt-0.5'}`}>{item.chart?.label || ''}</span>

      {big && (
        <span className={`mt-1 flex items-baseline gap-2 font-medium tabular-nums tracking-[-0.03em] leading-none ${featured ? 'text-[2.6rem]' : 'text-[2rem]'}`}>
          <Arrow dir={big.dir} valence={item.valence} size={featured ? 'text-[1.6rem]' : 'text-[1.3rem]'} />
          {big.text}
          {big.small && <small className="text-base font-normal tracking-normal text-fg-3">{big.small}</small>}
        </span>
      )}

      <span className={`mt-2 font-medium leading-snug tracking-[-0.01em] ${featured ? 'text-[1.15rem]' : 'text-[1.05rem]'}`}>
        {sentence}
      </span>

      <span className="mt-3 block w-full">
        <Sparkline chart={item.chart} valence={item.valence} height={featured ? 220 : 72} quiet />
      </span>

      {featured && item.kind === 'stack' && (
        <span className="mt-3 text-sm text-fg-3">Moved alongside it. We can’t say it caused it.</span>
      )}
    </button>
  );
};

export default MovingCard;
